import { useState, useEffect, useRef, useCallback } from "react";
import { COLORS, API } from "../constants";
import NoteEditor from "./NoteEditor";

export default function FileNotesModal({ file, onClose, token, setPreviewFile }) {
  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renamingTabId, setRenamingTabId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [tabContextMenu, setTabContextMenu] = useState(null);
  const [confirmCloseTabId, setConfirmCloseTabId] = useState(null);
  const [draggedTabId, setDraggedTabId] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState(null);
  const [isDraggingAttachmentOver, setIsDraggingAttachmentOver] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  const fetchNotes = async () => {
    setLoading(true);
    const res = await fetch(`${API}/files/${file.id}/notes`, { headers: authHeaders });
    const data = await res.json();
    const sorted = (data.notes || []).sort((a, b) => a.position - b.position);
    setNotes(sorted);
    if (sorted.length > 0) setActiveNoteId(prev => sorted.find(n => n.id === prev) ? prev : sorted[0].id);
    setLoading(false);
  };

  const activeNote = notes.find(n => n.id === activeNoteId);

  const handleAddTab = async () => {
    const res = await fetch(`${API}/files/${file.id}/notes`, {
      method: "POST", headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ tab_name: "New Tab" })
    });
    const newNote = await res.json();
    newNote.attachments = [];
    setNotes(prev => [...prev, newNote]);
    setActiveNoteId(newNote.id);
  };

  const updateContentLocally = (noteId, content) => {
    setNotes(prev => prev.map(n => n.id === noteId ? { ...n, content } : n));
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      fetch(`${API}/notes/${noteId}`, {
        method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ content })
      });
    }, 800);
  };

  const startRename = (note) => {
    setRenamingTabId(note.id);
    setRenameValue(note.tab_name);
    setTabContextMenu(null);
  };

  const commitRename = async () => {
    if (!renamingTabId) return;
    const newName = renameValue.trim() || "New Tab";
    setNotes(prev => prev.map(n => n.id === renamingTabId ? { ...n, tab_name: newName } : n));
    await fetch(`${API}/notes/${renamingTabId}`, {
      method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ tab_name: newName })
    });
    setRenamingTabId(null);
  };

  const requestCloseTab = (noteId) => {
    setConfirmCloseTabId(noteId);
    setTabContextMenu(null);
  };

  const confirmCloseTab = async () => {
    const noteId = confirmCloseTabId;
    setConfirmCloseTabId(null);
    await fetch(`${API}/notes/${noteId}`, { method: "DELETE", headers: authHeaders });
    const remaining = notes.filter(n => n.id !== noteId);
    setNotes(remaining);
    if (activeNoteId === noteId) {
      setActiveNoteId(remaining.length > 0 ? remaining[0].id : null);
    }
    if (remaining.length === 0) {
      // Auto-create a fresh tab so the popup is never empty
      await handleAddTab();
    }
  };

  const handleTabDragStart = (noteId) => setDraggedTabId(noteId);
  const handleTabDragOver = (e, noteId) => { e.preventDefault(); setDragOverTabId(noteId); };
  const handleTabDrop = async (e, targetId) => {
    e.preventDefault();
    setDragOverTabId(null);
    if (draggedTabId === null || draggedTabId === targetId) return;
    const reordered = [...notes];
    const fromIdx = reordered.findIndex(n => n.id === draggedTabId);
    const toIdx = reordered.findIndex(n => n.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setNotes(reordered);
    setDraggedTabId(null);
    await fetch(`${API}/files/${file.id}/notes/reorder`, {
      method: "PUT", headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify({ note_ids: reordered.map(n => n.id) })
    });
  };

  // ── Attachments ──
  const uploadAttachment = useCallback(async (fileObj) => {
    if (!activeNoteId) return;
    setUploadingAttachment(true);
    const formData = new FormData();
    formData.append("file", fileObj);
    const res = await fetch(`${API}/notes/${activeNoteId}/attachments`, { method: "POST", headers: authHeaders, body: formData });
    const attachment = await res.json();
    setNotes(prev => prev.map(n => n.id === activeNoteId ? { ...n, attachments: [...n.attachments, attachment] } : n));
    setUploadingAttachment(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNoteId]);

  const handleAttachmentPick = (e) => {
    const picked = Array.from(e.target.files);
    picked.forEach(uploadAttachment);
    e.target.value = "";
  };

  const removeAttachment = async (attachmentId) => {
    await fetch(`${API}/notes/attachments/${attachmentId}`, { method: "DELETE", headers: authHeaders });
    setNotes(prev => prev.map(n => ({ ...n, attachments: n.attachments.filter(a => a.id !== attachmentId) })));
  };

  const isRealFileDrag = (e) => e.dataTransfer.types.includes("Files");
  const handleAttachDragOver = (e) => { if (isRealFileDrag(e)) { e.preventDefault(); e.stopPropagation(); setIsDraggingAttachmentOver(true); } };
  const handleAttachDragLeave = (e) => { if (isRealFileDrag(e) && !e.currentTarget.contains(e.relatedTarget)) setIsDraggingAttachmentOver(false); };
  const handleAttachDrop = (e) => {
    if (!isRealFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachmentOver(false);
    Array.from(e.dataTransfer.files).forEach(uploadAttachment);
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
      onDragOver={e => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); e.stopPropagation(); } }}
      onDrop={e => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); e.stopPropagation(); } }}
    >
      <div style={{ background: COLORS.bg, borderRadius: "12px", width: "90%", maxWidth: "820px", height: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `0.5px solid ${COLORS.border}` }}>
          <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.filename}</span>
          <div style={{ display: "flex", gap: "8px", flexShrink: 0, marginLeft: "12px" }}>
            <button onClick={() => window.open(`${API}/files/${file.id}/download?token=${token}`)}
              style={{ padding: "6px 14px", fontSize: "13px", background: COLORS.blue, color: "white", border: "none", borderRadius: "7px", cursor: "pointer", fontWeight: "500" }}>
              ⬇️ Download
            </button>
            <button onClick={() => setPreviewFile({ id: file.id, filename: file.filename })}
              style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textPrimary, cursor: "pointer" }}>
              👁️ Preview
            </button>
            <button onClick={onClose}
              style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px", padding: "8px 12px 0", borderBottom: `0.5px solid ${COLORS.border}`, overflowX: "auto" }}>
          {notes.map(note => (
            <div
              key={note.id}
              draggable
              onDragStart={() => handleTabDragStart(note.id)}
              onDragOver={e => handleTabDragOver(e, note.id)}
              onDrop={e => handleTabDrop(e, note.id)}
              onContextMenu={e => { e.preventDefault(); setTabContextMenu({ x: e.clientX, y: e.clientY, note }); }}
              onClick={() => setActiveNoteId(note.id)}
              className="file-notes-tab"
              style={{
                display: "flex", alignItems: "center", gap: "6px", padding: "7px 10px", borderRadius: "7px 7px 0 0",
                cursor: "pointer", fontSize: "13px", maxWidth: "160px", flexShrink: 0,
                background: activeNoteId === note.id ? COLORS.bg : COLORS.bgSecondary,
                border: `0.5px solid ${dragOverTabId === note.id ? COLORS.blue : COLORS.border}`,
                borderBottom: activeNoteId === note.id ? `1px solid ${COLORS.bg}` : `0.5px solid ${COLORS.border}`,
                marginBottom: "-1px",
                color: activeNoteId === note.id ? COLORS.textPrimary : COLORS.textSecondary,
                fontWeight: activeNoteId === note.id ? "500" : "400"
              }}
            >
              {renamingTabId === note.id ? (
                <input
                  autoFocus value={renameValue}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingTabId(null); }}
                  onBlur={commitRename}
                  style={{ width: "90px", fontSize: "13px", padding: "1px 4px", borderRadius: "4px", border: `1px solid ${COLORS.blue}`, outline: "none" }}
                />
              ) : (
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{note.tab_name}</span>
              )}
              <span
                className="file-notes-tab-close"
                onClick={e => { e.stopPropagation(); requestCloseTab(note.id); }}
                style={{ fontSize: "12px", color: COLORS.textTertiary, cursor: "pointer", opacity: 0, transition: "opacity 0.1s", flexShrink: 0 }}
              >✕</span>
            </div>
          ))}
          <button onClick={handleAddTab} title="New tab"
            style={{ padding: "6px 10px", fontSize: "14px", background: "transparent", border: "none", color: COLORS.textSecondary, cursor: "pointer", flexShrink: 0 }}>
            +
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p style={{ fontSize: "13px", color: COLORS.textTertiary }}>Loading notes...</p>
          </div>
        ) : activeNote && (
          <div
            onDragOver={handleAttachDragOver} onDragLeave={handleAttachDragLeave} onDrop={handleAttachDrop}
            style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}
          >
            {isDraggingAttachmentOver && (
              <div style={{ position: "absolute", inset: "8px", border: `2px dashed ${COLORS.blue}`, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", background: "rgba(59,130,246,0.06)", zIndex: 5 }}>
                <span style={{ fontSize: "13px", color: COLORS.blue, fontWeight: "500" }}>Drop to attach</span>
              </div>
            )}
            <NoteEditor
              key={activeNote.id}
              content={activeNote.content}
              onChange={content => updateContentLocally(activeNote.id, content)}
              noteId={activeNote.id}
              token={token}
            />

            {/* Attachments */}
            <div style={{ borderTop: `0.5px solid ${COLORS.border}`, padding: "12px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {activeNote.attachments.map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 6px 4px 10px", borderRadius: "14px", background: COLORS.bgSecondary, border: `0.5px solid ${COLORS.border}`, fontSize: "12px" }}>
                    <span onClick={() => setPreviewFile({ id: `note-attachment-${a.id}`, filename: a.filename, isNoteAttachment: true, attachmentId: a.id })}
                      style={{ cursor: "pointer", color: COLORS.textPrimary, maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      📎 {a.filename}
                    </span>
                    <span onClick={() => removeAttachment(a.id)} style={{ cursor: "pointer", color: COLORS.textTertiary, fontSize: "11px" }}>✕</span>
                  </div>
                ))}
                <label style={{ fontSize: "12px", color: COLORS.blue, cursor: "pointer", padding: "4px 10px" }}>
                  {uploadingAttachment ? "Uploading…" : "+ Add Attachment"}
                  <input type="file" multiple onChange={handleAttachmentPick} style={{ display: "none" }} disabled={uploadingAttachment} />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab context menu */}
      {tabContextMenu && (
        <>
          <div onClick={() => setTabContextMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 1299 }} />
          <div style={{ position: "fixed", top: tabContextMenu.y, left: tabContextMenu.x, background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 1300, minWidth: "140px", padding: "4px 0" }} onClick={e => e.stopPropagation()}>
            <div onClick={() => startRename(tabContextMenu.note)} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.textPrimary }}>✏️ Rename</div>
            <div onClick={() => requestCloseTab(tabContextMenu.note.id)} style={{ padding: "8px 16px", fontSize: "13px", cursor: "pointer", color: COLORS.danger }}>✕ Close tab</div>
          </div>
        </>
      )}

      {/* Confirm close dialog */}
      {confirmCloseTabId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1301, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: COLORS.bg, borderRadius: "14px", padding: "22px", minWidth: "320px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: COLORS.textPrimary, marginBottom: "8px" }}>Close this tab?</h3>
            <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "16px" }}>This will permanently delete its notes and attachments. This can't be undone.</p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmCloseTabId(null)} style={{ padding: "7px 14px", fontSize: "13px", background: "transparent", border: `0.5px solid ${COLORS.border}`, borderRadius: "7px", color: COLORS.textSecondary, cursor: "pointer" }}>Cancel</button>
              <button onClick={confirmCloseTab} style={{ padding: "7px 14px", fontSize: "13px", background: COLORS.danger, color: "white", border: "none", borderRadius: "7px", cursor: "pointer", fontWeight: "500" }}>Close Tab</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}